import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { Pause, Play } from 'lucide-react';
import {
  toFullMediaUrl,
  toVideoVariantUrl,
  toVideoPosterUrl,
} from '../../utils/mediaUrl';
import VideoSoundButton from './VideoSoundButton';
import {
  releaseMediaPlayback,
  requestMediaPlayback,
  getMediaMutedPreference,
  setMediaMutedPreference,
  subscribeMediaMutedPreference,
  stopActiveMediaPlayback,
  unlockMediaAudio,
} from './mediaPlayback';
import './UnifiedVideo.css';

const DEFAULT_ROOT_MARGIN = '480px 0px';
const IOS_LOOP_LEAD_MS = 80;
const MOBILE_EDGE_HOLD_DELAY_MS = 180;

const isIOSWebKit = () => {
  if (typeof navigator === 'undefined') return false;
  const userAgent = navigator.userAgent || '';
  const ipadDesktopMode = navigator.platform === 'MacIntel'
    && Number(navigator.maxTouchPoints) > 1;
  return /iPad|iPhone|iPod/i.test(userAgent) || ipadDesktopMode;
};

const resolveCanonicalVideoSource = (value) => {
  const raw = String(value || '').trim();
  if (!raw || /^(blob:|data:)/i.test(raw)) return raw;
  // Plain test/demo values and local object URLs are already playable
  // sources. Only normalise actual stored trip-video paths to /files/.
  return /(?:^|\/)trip-videos\//i.test(raw) || /^https?:\/\//i.test(raw)
    ? (toFullMediaUrl(raw) || raw)
    : raw;
};

/**
 * The only video player used by the application.
 *
 * It starts muted by default so Safari/Chrome can autoplay the inline first
 * frame consistently. The Android native feed may opt into audible autoplay;
 * a tap on the video surface remains a play/pause gesture on every platform.
 */
const UnifiedVideo = forwardRef(function UnifiedVideo({
  src,
  poster,
  className = '',
  containerClassName = '',
  eager = false,
  active = true,
  visible,
  load,
  observeVisibility = true,
  visibilityThreshold = 0.25,
  rootMargin = DEFAULT_ROOT_MARGIN,
  autoplay = true,
  autoplayWithSound = false,
  resetOnActivate = false,
  showSoundButton = true,
  showPlayPauseButton = false,
  navigateOnClick = false,
  showMobileControls = false,
  showMobileProgress = true,
  preserveStagingPoster = false,
  preservePosterUntilFrame = false,
  soundButtonClassName = '',
  ariaLabel = 'Reproduzir vídeo',
  loop = true,
  preload,
  fetchPriority,
  onError,
  onLoadedMetadata,
  onLoadedData,
  onCanPlay,
  onPlaying,
  onEnded,
  onPlay,
  onPause,
  onTimeUpdate,
  onProgress,
  onUserPlay,
  onUserPause,
  onDoubleTap,
  children,
}, forwardedRef) {
  const ownerRef = useRef(Symbol('unified-video'));
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const playWithAudioRef = useRef(null);
  const lastTapRef = useRef(0);
  const tapResetTimerRef = useRef(null);
  const variantRetryTimerRef = useRef(null);
  const variantRetryAttemptRef = useRef(0);
  const posterRef = useRef(null);
  const posterHideFrameRef = useRef(null);
  const iosLoopTimerRef = useRef(null);
  const iosLoopRestartingRef = useRef(false);
  const edgeHoldTimerRef = useRef(null);
  const edgeHoldActiveRef = useRef(false);
  const touchStartPointRef = useRef(null);
  const touchMovedRef = useRef(false);
  const suppressNextClickRef = useRef(false);
  // Keep the media element attached after its first load. Mobile browsers
  // (especially Safari/WebKit) may discard the buffered resource when React
  // removes `src` as a card leaves the prefetch margin. Keeping the source
  // attached lets a feed pause/resume at the same playhead instead of
  // restarting from zero when the user scrolls back.
  const sourceAttachedRef = useRef(false);
  const sourceIdentityRef = useRef('');
  const previousActiveRef = useRef(active);
  // Keep the last native playhead separately from React state.  The state is
  // only needed for the mobile timeline and can lag behind a pause gesture;
  // this ref is what lets a later play() resume the exact same frame.
  const playheadRef = useRef(0);
  // Range inputs can emit dozens of events per frame while a finger drags.
  // Coalesce them into one native seek per animation frame so the playhead
  // feels continuous instead of stuttering like a sequence of jumps.
  const pendingSeekRef = useRef(null);
  const seekFrameRef = useRef(null);
  // React state updates are asynchronous. This immediate flag prevents a
  // pending autoplay/play() promise from turning the video back on after the
  // user has just tapped to pause it.
  const userPausedRef = useRef(false);
  const [nearViewport, setNearViewport] = useState(Boolean(eager));
  const [visibilityRatio, setVisibilityRatio] = useState(eager ? 1 : 0);
  const [manualLoad, setManualLoad] = useState(false);
  // Audible autoplay is opt-in and reserved for the native Android feed,
  // whose WebView explicitly permits media playback without a user gesture.
  const [muted, setMuted] = useState(
    autoplayWithSound ? getMediaMutedPreference() : true,
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [posterFailed, setPosterFailed] = useState(false);
  const [userPaused, setUserPaused] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isFastForwarding, setIsFastForwarding] = useState(false);
  const [framePainted, setFramePainted] = useState(false);
  const appleMobileWebKit = useMemo(isIOSWebKit, []);
  const canonicalSource = useMemo(() => resolveCanonicalVideoSource(src), [src]);
  const lastLoadedSourceRef = useRef(canonicalSource);
  const [compactViewport, setCompactViewport] = useState(() => (
    typeof window !== 'undefined' && window.innerWidth <= 768
  ));
  const mobileSource = useMemo(
    () => toVideoVariantUrl(canonicalSource, 'mobile'),
    [canonicalSource],
  );
  const desktopSource = useMemo(
    () => toVideoVariantUrl(canonicalSource, 'desktop') || canonicalSource,
    [canonicalSource],
  );
  const responsiveSource = compactViewport ? mobileSource : desktopSource;
  // Prefer the rendition sized for the current viewport. This matters most
  // on iOS/WebKit, whose media buffer is more conservative than Chromium's:
  // downloading a large camera original before trying the 720p encode
  // creates a long black delay. The server's media fallback resolves this
  // URL to the canonical source while an asynchronous rendition is not ready,
  // so newly-published trips still play immediately without a 404 probe.
  const sourceCandidates = useMemo(() => (
    [...new Set([
      responsiveSource,
      canonicalSource,
      mobileSource,
    ].filter(Boolean))]
  ), [canonicalSource, mobileSource, responsiveSource]);
  const preferredSource = sourceCandidates[0] || canonicalSource;
  const [sourceCandidate, setSourceCandidate] = useState(preferredSource);

  useEffect(() => {
    if (!autoplayWithSound) return undefined;
    const syncMutedPreference = (nextMuted) => {
      setMuted(nextMuted);
      if (videoRef.current) videoRef.current.muted = nextMuted;
    };
    return subscribeMediaMutedPreference(syncMutedPreference);
  }, [autoplayWithSound]);

  const hasExternalVisibility = typeof visible === 'boolean';
  const hasExternalLoad = typeof load === 'boolean';
  const effectiveNearViewport = hasExternalLoad ? load : nearViewport;
  const effectiveVisible = hasExternalVisibility
    ? visible
    : nearViewport && visibilityRatio >= visibilityThreshold;
  const shouldLoad = Boolean(src && (effectiveNearViewport || manualLoad));
  const shouldAutoplay = Boolean(
    autoplay && active && effectiveVisible && shouldLoad && !userPaused,
  );

  const posterUrl = poster
    ? (/^(blob:|data:)/i.test(String(poster)) ? poster : toFullMediaUrl(poster))
    : toVideoPosterUrl(src, { preserveStaging: preserveStagingPoster });
  const sourceIdentity = canonicalSource || String(src || '');
  if (sourceIdentityRef.current !== sourceIdentity) {
    sourceIdentityRef.current = sourceIdentity;
    sourceAttachedRef.current = false;
  }
  const sourceUrl = (sourceAttachedRef.current || shouldLoad)
    ? sourceCandidate
    : undefined;

  const clearVariantRetry = useCallback(() => {
    if (variantRetryTimerRef.current && typeof window !== 'undefined') {
      window.clearTimeout(variantRetryTimerRef.current);
    }
    variantRetryTimerRef.current = null;
  }, []);

  const clearPosterHideFrame = useCallback(() => {
    const pending = posterHideFrameRef.current;
    if (pending && typeof window !== 'undefined') {
      if (pending.type === 'video-frame') {
        pending.video?.cancelVideoFrameCallback?.(pending.id);
      } else {
        window.cancelAnimationFrame(pending.id);
      }
    }
    posterHideFrameRef.current = null;
  }, []);

  const clearIosLoopTimer = useCallback(() => {
    if (iosLoopTimerRef.current != null && typeof window !== 'undefined') {
      window.clearTimeout(iosLoopTimerRef.current);
    }
    iosLoopTimerRef.current = null;
  }, []);

  const revealPosterUntilFrame = useCallback(() => {
    if (!appleMobileWebKit && !preservePosterUntilFrame) return;
    clearPosterHideFrame();
    // Keep the real poster above the video until a decoded frame is painted.
    posterRef.current?.classList.remove('gm-unified-video__poster--hidden');
    setFramePainted(false);
  }, [appleMobileWebKit, clearPosterHideFrame, preservePosterUntilFrame]);

  const hidePosterAfterPaint = useCallback((video) => {
    if (!appleMobileWebKit && !preservePosterUntilFrame) return;
    clearPosterHideFrame();
    const finish = () => {
      posterHideFrameRef.current = null;
      posterRef.current?.classList.add('gm-unified-video__poster--hidden');
      setFramePainted(true);
    };
    // This callback is tied to an actually presented decoded frame. It is
    // stronger than `playing` or `canplay`, which may fire while the video
    // layer is still blank on Safari, Android or desktop browsers.
    if (video && typeof video.requestVideoFrameCallback === 'function') {
      const id = video.requestVideoFrameCallback(finish);
      posterHideFrameRef.current = { type: 'video-frame', id, video };
      return;
    }
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
      finish();
      return;
    }
    // When presented-frame callbacks are unavailable, two paints keep the
    // real first-frame poster in place until the video layer is ready.
    const firstId = window.requestAnimationFrame(() => {
      const secondId = window.requestAnimationFrame(() => {
        finish();
      });
      posterHideFrameRef.current = { type: 'animation-frame', id: secondId };
    });
    posterHideFrameRef.current = { type: 'animation-frame', id: firstId };
  }, [appleMobileWebKit, clearPosterHideFrame, preservePosterUntilFrame]);

  const restartIosLoop = useCallback((video) => {
    if (!appleMobileWebKit || !loop || !video || !shouldAutoplay
        || iosLoopRestartingRef.current) return;
    iosLoopRestartingRef.current = true;
    clearIosLoopTimer();
    revealPosterUntilFrame();
    try {
      video.currentTime = 0;
      const playResult = video.play();
      playResult?.catch?.(() => {
        iosLoopRestartingRef.current = false;
      });
    } catch (_) {
      iosLoopRestartingRef.current = false;
    }
  }, [appleMobileWebKit, clearIosLoopTimer, loop, revealPosterUntilFrame, shouldAutoplay]);

  const scheduleIosLoop = useCallback((video) => {
    if (!appleMobileWebKit || !loop || !shouldAutoplay || !video
        || !Number.isFinite(video.duration) || video.duration <= 0) return;
    clearIosLoopTimer();
    const remainingMs = Math.max(0, (video.duration - video.currentTime) * 1000);
    iosLoopTimerRef.current = window.setTimeout(
      () => restartIosLoop(video),
      Math.max(0, remainingMs - IOS_LOOP_LEAD_MS),
    );
  }, [appleMobileWebKit, clearIosLoopTimer, loop, restartIosLoop, shouldAutoplay]);

  const retryPreferredVariant = useCallback(() => {
    if (!responsiveSource || responsiveSource === canonicalSource) return false;
    // Encoding runs asynchronously on the VPS. A large camera upload can take
    // longer than the first few seconds on a single CPU worker, so keep
    // retrying the preferred H.264 sibling for about two minutes instead of
    // permanently falling back to an unplayable camera source.
    const delays = [1000, 3000, 8000, 15000, 30000, 60000];
    const attempt = variantRetryAttemptRef.current;
    if (attempt >= delays.length || typeof window === 'undefined') return false;
    clearVariantRetry();
    variantRetryTimerRef.current = window.setTimeout(() => {
      variantRetryTimerRef.current = null;
      variantRetryAttemptRef.current += 1;
      setSourceCandidate(responsiveSource);
      setVideoReady(false);
      setPosterFailed(false);
      setFramePainted(false);

      // A media element that already emitted `error` will not retry merely
      // because React receives the same src string again. Explicitly reload
      // it when the background encoder may have just created the sibling.
      const video = videoRef.current;
      if (video) {
        try {
          video.pause();
          video.src = responsiveSource;
          video.load();
        } catch (_) {
          // The next React render still applies the candidate source.
        }
      }
    }, delays[attempt]);
    return true;
  }, [canonicalSource, clearVariantRetry, responsiveSource]);

  useEffect(() => {
    const preservePlayhead = lastLoadedSourceRef.current === canonicalSource
      && playheadRef.current > 0;
    const resumeAt = preservePlayhead ? playheadRef.current : 0;
    lastLoadedSourceRef.current = canonicalSource;
    clearVariantRetry();
    variantRetryAttemptRef.current = 0;
    sourceAttachedRef.current = false;
    setSourceCandidate(preferredSource);
    setVideoReady(false);
    setPosterFailed(false);
    setIsPlaying(false);
    setUserPaused(false);
    userPausedRef.current = false;
    setDuration(0);
    setCurrentTime(resumeAt);
    setIsFastForwarding(false);
    edgeHoldActiveRef.current = false;
    if (edgeHoldTimerRef.current && typeof window !== 'undefined') {
      window.clearTimeout(edgeHoldTimerRef.current);
    }
    edgeHoldTimerRef.current = null;
    if (videoRef.current) videoRef.current.playbackRate = 1;
    setMuted(autoplayWithSound ? getMediaMutedPreference() : true);
    setFramePainted(false);
    iosLoopRestartingRef.current = false;
    clearIosLoopTimer();
    clearPosterHideFrame();

    // A responsive rendition change can reattach the same trip video. Restore
    // its playhead after metadata is available instead of letting video.load()
    // silently jump back to the first frame.
    const video = videoRef.current;
    if (!video || resumeAt <= 0) return undefined;
    const restorePlayhead = () => {
      const maxTime = Number.isFinite(video.duration) && video.duration > 0
        ? Math.max(0, video.duration - 0.05)
        : resumeAt;
      try {
        video.currentTime = Math.min(resumeAt, maxTime);
      } catch (_) {
        // Metadata may still be settling; the next native event can retry.
      }
    };
    video.addEventListener('loadedmetadata', restorePlayhead, { once: true });
    if (video.readyState >= 1) restorePlayhead();
    return () => video.removeEventListener('loadedmetadata', restorePlayhead);
  }, [autoplayWithSound, canonicalSource, clearIosLoopTimer, clearPosterHideFrame, clearVariantRetry, preferredSource, src]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const updateViewport = () => setCompactViewport(window.innerWidth <= 768);
    window.addEventListener('resize', updateViewport, { passive: true });
    return () => window.removeEventListener('resize', updateViewport);
  }, []);

  useEffect(() => {
    if (!observeVisibility || hasExternalVisibility || hasExternalLoad) return undefined;
    const element = containerRef.current;
    if (!element || typeof window === 'undefined') return undefined;
    if (typeof window.IntersectionObserver !== 'function') {
      setNearViewport(true);
      setVisibilityRatio(1);
      return undefined;
    }
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        const ratio = entry?.isIntersecting ? entry.intersectionRatio : 0;
        setNearViewport(Boolean(entry?.isIntersecting));
        setVisibilityRatio(ratio);
      },
      { rootMargin, threshold: [0, visibilityThreshold, 0.5, 0.75, 1] },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [hasExternalLoad, hasExternalVisibility, observeVisibility, rootMargin, visibilityThreshold]);

  const playWithAudio = useCallback(async (userInitiated = false, requestedMuted) => {
    const video = videoRef.current;
    const playableSource = sourceUrl || (userInitiated
      ? (canonicalSource || sourceCandidate || src)
      : '');
    if (!video || !playableSource) return false;
    if (!userInitiated && !shouldAutoplay) return false;
    if (!userInitiated && userPausedRef.current) return false;

    // A very fast tap can arrive before IntersectionObserver has delivered
    // its first callback. Make that gesture self-sufficient instead of
    // requiring a second tap just to attach the source.
    if (!sourceUrl && userInitiated) {
      setManualLoad(true);
      if (canonicalSource && sourceCandidate !== canonicalSource) {
        setSourceCandidate(canonicalSource);
      }
      if (video.src !== playableSource) {
        video.src = playableSource;
      }
    }

    const priority = userInitiated ? 2 : (visibilityRatio || (effectiveVisible ? 1 : 0));
    if (!requestMediaPlayback(ownerRef.current, video, priority)) return false;

    // On touch devices an audible autoplay attempt is rejected before the
    // browser paints the first frame. Start muted immediately so the poster
    // and video are visible without waiting for a rejected play() promise.
    // A user tap still unmutes the same already-buffered element.
    const shouldMute = userInitiated
      ? (typeof requestedMuted === 'boolean' ? requestedMuted : muted)
      : (autoplayWithSound ? getMediaMutedPreference() : true);
    if (muted !== shouldMute) setMuted(shouldMute);
    video.muted = shouldMute;
    // Some mobile browsers clear currentTime while a source is being
    // reattached. Put the remembered playhead back before resuming, but never
    // interfere with a deliberate slide change (which explicitly resets the
    // native element in MediaCarousel).
    if (playheadRef.current > 0 && video.currentTime < 0.05
        && Number.isFinite(video.duration) && video.duration > 0) {
      try {
        video.currentTime = Math.min(playheadRef.current, Math.max(0, video.duration - 0.05));
      } catch (_) {
        // The browser will restore it from the metadata handler if needed.
      }
    }
    try {
      await video.play();
      if (userPausedRef.current) {
        // A tap can pause the element while the previous play promise is
        // still pending. Do not report that stale request as a new play.
        video.pause();
        releaseMediaPlayback(ownerRef.current, video);
        return false;
      }
      if (!shouldMute && userInitiated) unlockMediaAudio();
      onUserPlay?.(userInitiated);
      return true;
    } catch (_) {
      if (userPausedRef.current) {
        releaseMediaPlayback(ownerRef.current, video);
        return false;
      }
      // Autoplay with audio is commonly rejected by desktop Chrome/Safari.
      // Retry immediately muted so the first frame is still rendered; the
      // next user gesture can unmute it without another network request.
      if (!shouldMute && video.isConnected !== false) {
        // Do not make every following card repeat a browser-rejected audible
        // autoplay attempt. A real tap on this video or its sound icon can
        // still re-enable audio immediately.
        if (userInitiated) setMediaMutedPreference(true);
        setMuted(true);
        video.muted = true;
        try {
          await video.play();
          if (userPausedRef.current) {
            video.pause();
            releaseMediaPlayback(ownerRef.current, video);
            return false;
          }
          onUserPlay?.(userInitiated);
          return true;
        } catch (mutedError) {
          releaseMediaPlayback(ownerRef.current, video);
          return false;
        }
      }
      releaseMediaPlayback(ownerRef.current, video);
      return false;
    }
  }, [autoplayWithSound, canonicalSource, effectiveVisible, muted, onUserPlay, shouldAutoplay, sourceCandidate, sourceUrl, src, visibilityRatio]);

  playWithAudioRef.current = playWithAudio;

  useImperativeHandle(forwardedRef, () => ({
    element: videoRef.current,
    play: (userInitiated = true) => {
      userPausedRef.current = false;
      return playWithAudioRef.current?.(userInitiated, muted);
    },
    seek: (time) => {
      const video = videoRef.current;
      const nextTime = Number(time);
      if (!video || !Number.isFinite(nextTime)) return;
      const maxTime = Number.isFinite(video.duration) && video.duration > 0
        ? video.duration
        : Number.POSITIVE_INFINITY;
      const clampedTime = Math.max(0, Math.min(nextTime, maxTime));
      pendingSeekRef.current = clampedTime;
      playheadRef.current = clampedTime;
      if (showMobileControls) setCurrentTime(clampedTime);
      if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
        try { video.currentTime = clampedTime; } catch (_) { /* wait for metadata */ }
        return;
      }
      if (seekFrameRef.current != null) return;
      seekFrameRef.current = window.requestAnimationFrame(() => {
        seekFrameRef.current = null;
        const pending = pendingSeekRef.current;
        pendingSeekRef.current = null;
        if (pending == null || !videoRef.current) return;
        try { videoRef.current.currentTime = pending; } catch (_) { /* wait for metadata */ }
      });
    },
    pause: () => {
      videoRef.current?.pause();
      releaseMediaPlayback(ownerRef.current, videoRef.current);
    },
    togglePlay: () => {
      const video = videoRef.current;
      if (!video) return;
      if (video.paused || userPausedRef.current) {
        userPausedRef.current = false;
        setUserPaused(false);
        playWithAudioRef.current?.(true, muted);
      } else {
        userPausedRef.current = true;
        setUserPaused(true);
        video.pause();
        onUserPause?.();
      }
    },
  }), [muted, onUserPause, showMobileControls]);

  useEffect(() => {
    const wasActive = previousActiveRef.current;
    previousActiveRef.current = active;
    if (active && !wasActive && resetOnActivate) {
      userPausedRef.current = false;
      setUserPaused(false);
    }
    // When a carousel changes slides, MediaCarousel resets the outgoing
    // element's native currentTime. Do not carry that old playhead into a
    // later activation. A user pause keeps `userPaused` true and therefore
    // remains resumable.
    if (!active && !userPaused) playheadRef.current = 0;
  }, [active, resetOnActivate, userPaused]);

  // Safari can leave a dynamically attached video in a metadata-only state
  // unless it receives an explicit load() after the source is assigned. Do
  // this only when the URL changes: calling load() on every visibility change
  // would reset the playhead and reproduce the feed bug we are fixing.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !sourceUrl) return undefined;
    sourceAttachedRef.current = true;
    try {
      video.defaultMuted = true;
      video.muted = muted;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', 'true');
      // jsdom exposes load() but intentionally throws because it has no media
      // pipeline. Keep the real browser fix while keeping unit-test output
      // quiet and deterministic.
      const isJsdom = typeof navigator !== 'undefined'
        && /jsdom/i.test(navigator.userAgent || '');
      if (!isJsdom) video.load();
    } catch (_) {
      // Native playback will still use the declarative src/autoplay props.
    }
    return undefined;
    // `muted` deliberately is not a dependency. Changing sound must never
    // reload the file or move the currentTime on iOS.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceUrl]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    const shouldBePlaying = shouldAutoplay;
    if (!shouldBePlaying) {
      video.pause();
      releaseMediaPlayback(ownerRef.current, video);
      // iOS may discard the paused composited layer when a card leaves the
      // viewport. Prepare its real poster before it returns so there is no
      // white/black interval while the decoder wakes again.
      if ((appleMobileWebKit || preservePosterUntilFrame) && !userPaused) {
        revealPosterUntilFrame();
      }
      // Pausing because of a user gesture, a feed scroll or the one-player
      // mutex must never move the playhead. Slide changes reset the outgoing
      // video explicitly in MediaCarousel.stopAllVideos().
      return undefined;
    }

    playWithAudioRef.current?.(false, true);
    return undefined;
  }, [active, appleMobileWebKit, effectiveVisible, preservePosterUntilFrame, revealPosterUntilFrame, shouldAutoplay, sourceUrl, userPaused]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        revealPosterUntilFrame();
        stopActiveMediaPlayback();
        return;
      }
      // Returning to an installed iOS PWA does not necessarily trigger a new
      // IntersectionObserver notification. Resume explicitly and keep the
      // poster until requestVideoFrameCallback confirms a painted frame.
      if (shouldAutoplay) playWithAudioRef.current?.(false, true);
    };
    if (typeof document === 'undefined') return undefined;
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [revealPosterUntilFrame, shouldAutoplay]);

  useEffect(() => () => {
    const video = videoRef.current;
    video?.pause?.();
    releaseMediaPlayback(ownerRef.current, video);
    clearVariantRetry();
    clearIosLoopTimer();
    clearPosterHideFrame();
    if (tapResetTimerRef.current && typeof window !== 'undefined') {
      window.clearTimeout(tapResetTimerRef.current);
    }
    if (edgeHoldTimerRef.current && typeof window !== 'undefined') {
      window.clearTimeout(edgeHoldTimerRef.current);
    }
    if (seekFrameRef.current != null && typeof window !== 'undefined') {
      window.cancelAnimationFrame?.(seekFrameRef.current);
    }
    seekFrameRef.current = null;
    pendingSeekRef.current = null;
    edgeHoldTimerRef.current = null;
    edgeHoldActiveRef.current = false;
    if (video) video.playbackRate = 1;
  }, [clearIosLoopTimer, clearPosterHideFrame, clearVariantRetry]);

  const toggleMute = useCallback((event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    const video = videoRef.current;
    const nextMuted = !muted;
    setMediaMutedPreference(nextMuted);
    setMuted(nextMuted);
    if (!video) return;
    video.muted = nextMuted;
    if (!nextMuted && video.paused) {
      userPausedRef.current = false;
      setUserPaused(false);
      playWithAudioRef.current?.(true, false);
    }
  }, [muted]);

  // Mobile Home feed: TikTok-style full-surface hold. After a short hold the
  // current video temporarily runs at 2× and returns to normal speed as soon
  // as the finger leaves. Movement cancels the hold immediately so a swipe
  // can still reach MediaCarousel without being turned into a tap.
  const stopEdgeHold = useCallback(() => {
    if (edgeHoldTimerRef.current && typeof window !== 'undefined') {
      window.clearTimeout(edgeHoldTimerRef.current);
    }
    edgeHoldTimerRef.current = null;
    edgeHoldActiveRef.current = false;
    const video = videoRef.current;
    if (video && video.playbackRate !== 1) video.playbackRate = 1;
    setIsFastForwarding(false);
  }, []);

  const startEdgeHold = useCallback(() => {
    if (!showMobileControls) return;
    stopEdgeHold();
    if (typeof window === 'undefined') return;
    edgeHoldTimerRef.current = window.setTimeout(() => {
      const video = videoRef.current;
      if (!video) return;
      // A long press is an explicit playback gesture. If the video was
      // paused, resume it before applying the temporary 2x rate so the
      // gesture never appears to do nothing on mobile Safari/PWA.
      if (video.paused || userPausedRef.current) {
        userPausedRef.current = false;
        setUserPaused(false);
        playWithAudioRef.current?.(true, muted);
      }
      video.playbackRate = 2;
      edgeHoldActiveRef.current = true;
      setIsFastForwarding(true);
    }, MOBILE_EDGE_HOLD_DELAY_MS);
  }, [muted, showMobileControls, stopEdgeHold]);

  const getTouchPoint = useCallback((event) => (
    event?.touches?.[0] || event?.changedTouches?.[0] || null
  ), []);

  const handleVideoTouchStart = useCallback((event) => {
    if (!showMobileControls) return;
    const point = getTouchPoint(event);
    if (!point) return;
    touchStartPointRef.current = { x: point.clientX, y: point.clientY };
    touchMovedRef.current = false;
    suppressNextClickRef.current = false;
    startEdgeHold();
  }, [getTouchPoint, showMobileControls, startEdgeHold]);

  const handleVideoTouchMove = useCallback((event) => {
    if (!showMobileControls || !touchStartPointRef.current) return;
    const point = getTouchPoint(event);
    if (!point) return;
    const dx = point.clientX - touchStartPointRef.current.x;
    const dy = point.clientY - touchStartPointRef.current.y;
    // A small tolerance absorbs iOS touch jitter; once crossed, hand the
    // gesture back to the carousel/page and never let the hold timer fire.
    if (Math.hypot(dx, dy) >= 10) {
      touchMovedRef.current = true;
      stopEdgeHold();
    }
  }, [getTouchPoint, showMobileControls, stopEdgeHold]);

  const handleVideoTouchEnd = useCallback((event) => {
    if (!showMobileControls) return;
    const consumed = touchMovedRef.current || edgeHoldActiveRef.current;
    if (consumed) suppressNextClickRef.current = true;
    stopEdgeHold();
    touchStartPointRef.current = null;
    touchMovedRef.current = false;
    // Keep the event available to MediaCarousel. Only the synthetic click
    // generated afterwards is suppressed when this was a swipe/long hold.
    void event;
  }, [showMobileControls, stopEdgeHold]);

  const seekFromProgress = useCallback((event) => {
    event.stopPropagation();
    const video = videoRef.current;
    const nextTime = Number(event.currentTarget.value);
    if (!video || !Number.isFinite(nextTime)) return;
    const maxTime = Number.isFinite(video.duration) && video.duration > 0
      ? video.duration
      : Number.POSITIVE_INFINITY;
    const clampedTime = Math.max(0, Math.min(nextTime, maxTime));
    pendingSeekRef.current = clampedTime;
    playheadRef.current = clampedTime;
    if (showMobileControls) setCurrentTime(clampedTime);
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
      try { video.currentTime = clampedTime; } catch (_) { /* wait for metadata */ }
      return;
    }
    if (seekFrameRef.current != null) return;
    seekFrameRef.current = window.requestAnimationFrame(() => {
      seekFrameRef.current = null;
      const pending = pendingSeekRef.current;
      pendingSeekRef.current = null;
      if (pending == null || !videoRef.current) return;
      try { videoRef.current.currentTime = pending; } catch (_) { /* wait for metadata */ }
    });
  }, [showMobileControls]);

  const performVideoTap = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused || userPausedRef.current) {
      userPausedRef.current = false;
      setUserPaused(false);
      // The video surface controls play/pause only. Audio is deliberately
      // exclusive to VideoSoundButton so iOS, Android, desktop and installed
      // PWAs have the same interaction contract.
      playWithAudioRef.current?.(true, muted);
      return;
    }
    userPausedRef.current = true;
    setUserPaused(true);
    video.pause();
    onUserPause?.();
  }, [muted, onUserPause]);

  const handleVideoClick = useCallback((event) => {
    if (suppressNextClickRef.current) {
      suppressNextClickRef.current = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    // Some card layouts make the entire media surface a navigation target.
    // Leave the event untouched so the surrounding router Link can handle it;
    // the dedicated play/pause button remains available for playback control.
    if (navigateOnClick) return;
    event.preventDefault();
    event.stopPropagation();
    if (onDoubleTap) {
      const now = Date.now();
      if (lastTapRef.current && now - lastTapRef.current <= 280) {
        lastTapRef.current = 0;
        if (tapResetTimerRef.current) window.clearTimeout(tapResetTimerRef.current);
        tapResetTimerRef.current = null;
        onDoubleTap(event);
        return;
      }
      lastTapRef.current = now;
      if (tapResetTimerRef.current) window.clearTimeout(tapResetTimerRef.current);
      tapResetTimerRef.current = window.setTimeout(() => {
        lastTapRef.current = 0;
        tapResetTimerRef.current = null;
      }, 280);
    }
    performVideoTap();
  }, [navigateOnClick, onDoubleTap, performVideoTap]);

  const handlePlayPauseClick = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    performVideoTap();
  }, [performVideoTap]);

  const handleError = useCallback((event) => {
    const video = event.currentTarget;
    // The canonical source is the first choice because it is promoted before
    // the trip row is committed. If an old row points at a missing original,
    // walk through the generated renditions without making the user wait for
    // a probe request. This also handles desktop rows where `-desktop.mp4`
    // is absent but the mobile rendition is already available.
    const currentIndex = sourceCandidates.indexOf(sourceCandidate);
    const nextSource = currentIndex >= 0
      ? sourceCandidates.slice(currentIndex + 1).find((candidate) => candidate !== sourceCandidate)
      : null;
    if (nextSource) {
      clearVariantRetry();
      setSourceCandidate(nextSource);
      setVideoReady(false);
      setPosterFailed(false);
      setFramePainted(false);
      try {
        video.pause();
        video.src = nextSource;
        video.load();
      } catch (_) {
        // React will still apply the next candidate on the following render.
      }
      return;
    }
    // iPhone uploads are often MOV/HEVC. Their compatible MP4 sibling is
    // generated asynchronously after the upload response. Do not give up
    // permanently while the background worker finishes.
    const retrying = retryPreferredVariant();
    video.pause();
    setIsPlaying(false);
    setVideoReady(false);
    setFramePainted(false);
    releaseMediaPlayback(ownerRef.current, video);
    // Keep the component (and its real poster) mounted while a generated
    // mobile rendition is still being produced. Notify the parent only after
    // the bounded retry window is exhausted.
    if (!retrying) onError?.(event);
  }, [clearVariantRetry, onError, retryPreferredVariant, sourceCandidate, sourceCandidates]);

  if (!src) return null;

  return (
    <span
      ref={containerRef}
      className={`gm-unified-video ${containerClassName}`.trim()}
      data-playing={isPlaying ? 'true' : 'false'}
      data-muted={muted ? 'true' : 'false'}
      data-ready={videoReady ? 'true' : 'false'}
    >
      <video
        ref={videoRef}
        className={className}
        src={sourceUrl}
        // Keep the native poster attached even before the source is loaded.
        // This prevents the browser's blank video surface from replacing the
        // real first-frame preview during source attachment.
        poster={posterUrl || undefined}
        // Native autoplay starts the fetch/play pipeline in the same render
        // as the visible source. The effect below remains as a fallback for
        // browsers that require an explicit play() call.
        autoPlay={shouldAutoplay}
        muted={muted}
        playsInline
        // Safari still recognises this legacy inline hint in some installed
        // PWA versions even when the React `playsInline` property is set.
        webkit-playsinline="true"
        // Safari on iPhone/iPad briefly clears the composited video layer at
        // a native loop boundary. The controlled loop below restarts just
        // before that boundary while the first-frame poster covers the seek.
        loop={loop && !appleMobileWebKit}
        preload={preload || (shouldLoad ? 'auto' : 'none')}
        fetchPriority={fetchPriority || (effectiveVisible ? 'high' : 'low')}
        aria-label={ariaLabel}
        onLoadedMetadata={(event) => {
          onLoadedMetadata?.(event);
          const nextDuration = event.currentTarget.duration;
          if (showMobileControls && Number.isFinite(nextDuration) && nextDuration > 0) {
            setDuration(nextDuration);
            setCurrentTime(Math.min(event.currentTarget.currentTime || 0, nextDuration));
          }
          playheadRef.current = Math.max(0, event.currentTarget.currentTime || 0);
          onProgress?.(
            Math.max(0, event.currentTarget.currentTime || 0),
            Number.isFinite(nextDuration) ? Math.max(0, nextDuration) : 0,
          );
          // WebKit can emit metadata before canplay while the first frame is
          // already available. Retrying here removes the extra tap/play
          // delay seen in iOS Safari and installed PWAs.
          if (shouldAutoplay) playWithAudioRef.current?.(false);
        }}
        onLoadedData={(event) => {
          clearVariantRetry();
          variantRetryAttemptRef.current = 0;
          setVideoReady(true);
          onLoadedData?.(event);
          if (shouldAutoplay) playWithAudioRef.current?.(false);
        }}
        onCanPlay={(event) => {
          setVideoReady(true);
          onCanPlay?.(event);
          if (shouldAutoplay) playWithAudioRef.current?.(false);
        }}
        onPlaying={(event) => {
          setIsPlaying(true);
          setVideoReady(true);
          iosLoopRestartingRef.current = false;
          hidePosterAfterPaint(event.currentTarget);
          scheduleIosLoop(event.currentTarget);
          onPlaying?.(event);
        }}
        onSeeked={(event) => {
          if (appleMobileWebKit && iosLoopRestartingRef.current) {
            iosLoopRestartingRef.current = false;
            hidePosterAfterPaint(event.currentTarget);
            scheduleIosLoop(event.currentTarget);
          }
        }}
        onEnded={(event) => {
          // Timer throttling or a background/foreground transition can still
          // let WebKit reach `ended`; cover and restart synchronously here as
          // a final guard against its white/black terminal canvas.
          if (appleMobileWebKit && loop && shouldAutoplay) {
            iosLoopRestartingRef.current = false;
            restartIosLoop(event.currentTarget);
          }
          playheadRef.current = 0;
          if (showMobileControls) setCurrentTime(0);
          onProgress?.(0, Number.isFinite(event.currentTarget.duration) ? event.currentTarget.duration : duration);
          onEnded?.(event);
        }}
        onPlay={(event) => {
          setIsPlaying(true);
          // Safari can emit `play` before its native layer has painted. Mark
          // the resource ready here, but let `playing` plus
          // requestVideoFrameCallback decide when the iOS poster can leave.
          setVideoReady(true);
          onPlay?.(event);
        }}
        onPause={(event) => {
          setIsPlaying(false);
          playheadRef.current = Math.max(0, event.currentTarget.currentTime || 0);
          clearIosLoopTimer();
          // Do not infer a manual pause from the native event. This callback
          // also runs when the global one-player mutex pauses this element
          // for another visible feed card, when the card leaves the viewport,
          // or when the browser briefly interrupts playback. Those pauses
          // must remain resumable. The explicit surface/imperative handlers
          // above are the only places that set `userPaused` to true.
          releaseMediaPlayback(ownerRef.current, event.currentTarget);
          onPause?.(event);
        }}
        onTimeUpdate={(event) => {
          onTimeUpdate?.(event);
          const nextDuration = event.currentTarget.duration;
          const nextCurrentTime = Math.max(0, event.currentTarget.currentTime || 0);
          playheadRef.current = nextCurrentTime;
          if (showMobileControls && Number.isFinite(nextDuration) && nextDuration > 0) {
            setDuration(nextDuration);
            setCurrentTime(Math.min(nextCurrentTime, nextDuration));
          }
          onProgress?.(nextCurrentTime, Number.isFinite(nextDuration) ? Math.max(0, nextDuration) : 0);
          // Re-arm after iOS resumes a media element without dispatching a
          // fresh `playing` event (seen after Control Centre interruptions).
          if (appleMobileWebKit && loop && shouldAutoplay
              && iosLoopTimerRef.current == null && !iosLoopRestartingRef.current) {
            scheduleIosLoop(event.currentTarget);
          }
        }}
        onTouchStart={handleVideoTouchStart}
        onTouchMove={handleVideoTouchMove}
        onTouchEnd={handleVideoTouchEnd}
        onTouchCancel={handleVideoTouchEnd}
        onClick={handleVideoClick}
        onError={handleError}
      />
      {posterUrl && !posterFailed && (
        preservePosterUntilFrame
          ? (!framePainted || appleMobileWebKit)
          : (!videoReady || appleMobileWebKit)
      ) && (
        <img
          ref={posterRef}
          className={`gm-unified-video__poster ${appleMobileWebKit && framePainted ? 'gm-unified-video__poster--hidden' : ''}`.trim()}
          src={posterUrl}
          alt=""
          aria-hidden="true"
          loading={effectiveVisible || eager || ((appleMobileWebKit || preservePosterUntilFrame) && effectiveNearViewport) ? 'eager' : 'lazy'}
          decoding="async"
          fetchPriority={effectiveVisible || (appleMobileWebKit && effectiveNearViewport) ? 'high' : 'low'}
          onError={() => setPosterFailed(true)}
        />
      )}
      {!navigateOnClick && !isPlaying && (userPaused || (!autoplay && !shouldLoad)) && (
        <button
          type="button"
          className="gm-unified-video__play"
          onClick={handleVideoClick}
          aria-label="Reproduzir vídeo"
        >
          <Play size={18} strokeWidth={2.4} fill="currentColor" />
        </button>
      )}
      {showSoundButton && (
        <VideoSoundButton
          muted={muted}
          onToggle={toggleMute}
          className={soundButtonClassName}
        />
      )}
      {showPlayPauseButton && (
        <button
          type="button"
          className="gm-video-play-button"
          onClick={handlePlayPauseClick}
          aria-label={isPlaying ? 'Pausar vídeo' : 'Reproduzir vídeo'}
          title={isPlaying ? 'Pausar vídeo' : 'Reproduzir vídeo'}
        >
          {isPlaying
            ? <Pause size={16} strokeWidth={2.2} fill="currentColor" />
            : <Play size={16} strokeWidth={2.2} fill="currentColor" />}
        </button>
      )}
      {showMobileControls && (
        <div className="gm-unified-video__mobile-controls" aria-label="Controlos do vídeo">
          {showMobileProgress && (
            <div className="gm-unified-video__progress" onClick={(event) => event.stopPropagation()}>
              <input
                type="range"
                min="0"
                max={duration > 0 ? duration : 1}
                step="0.01"
                value={duration > 0 ? Math.min(currentTime, duration) : 0}
                style={{ '--gm-video-progress': `${duration > 0 ? (Math.min(currentTime, duration) / duration) * 100 : 0}%` }}
                aria-label="Progresso do vídeo"
                onPointerDown={(event) => event.stopPropagation()}
                onPointerMove={(event) => event.stopPropagation()}
                onPointerUp={(event) => event.stopPropagation()}
                onTouchStart={(event) => event.stopPropagation()}
                onTouchMove={(event) => { event.stopPropagation(); event.preventDefault(); }}
                onTouchEnd={(event) => event.stopPropagation()}
                onInput={seekFromProgress}
                onChange={seekFromProgress}
              />
            </div>
          )}
          {isFastForwarding && (
            <div className="gm-unified-video__speed-indicator" role="status" aria-live="polite">
              2×
            </div>
          )}
        </div>
      )}
      {children}
    </span>
  );
});

UnifiedVideo.propTypes = {
  src: PropTypes.string,
  poster: PropTypes.string,
  className: PropTypes.string,
  containerClassName: PropTypes.string,
  eager: PropTypes.bool,
  active: PropTypes.bool,
  visible: PropTypes.bool,
  load: PropTypes.bool,
  observeVisibility: PropTypes.bool,
  visibilityThreshold: PropTypes.number,
  rootMargin: PropTypes.string,
  autoplay: PropTypes.bool,
  resetOnActivate: PropTypes.bool,
  showSoundButton: PropTypes.bool,
  showPlayPauseButton: PropTypes.bool,
  navigateOnClick: PropTypes.bool,
  showMobileControls: PropTypes.bool,
  showMobileProgress: PropTypes.bool,
  preserveStagingPoster: PropTypes.bool,
  preservePosterUntilFrame: PropTypes.bool,
  soundButtonClassName: PropTypes.string,
  ariaLabel: PropTypes.string,
  loop: PropTypes.bool,
  preload: PropTypes.oneOf(['none', 'metadata', 'auto']),
  fetchPriority: PropTypes.oneOf(['high', 'low', 'auto']),
  onError: PropTypes.func,
  onLoadedMetadata: PropTypes.func,
  onLoadedData: PropTypes.func,
  onCanPlay: PropTypes.func,
  onPlaying: PropTypes.func,
  onEnded: PropTypes.func,
  onPlay: PropTypes.func,
  onPause: PropTypes.func,
  onTimeUpdate: PropTypes.func,
  onProgress: PropTypes.func,
  onUserPlay: PropTypes.func,
  onUserPause: PropTypes.func,
  onDoubleTap: PropTypes.func,
  children: PropTypes.node,
};

export default UnifiedVideo;
